import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type {
  AppointmentRule,
  CreateAppointmentRuleRequest,
  UpdateAppointmentRuleRequest,
} from 'models/appointments';
import { appointmentRuleTypeLabels } from 'models/appointments';
import useAppointmentRules from 'hooks/useAppointmentRules';
import ConfirmDialog from 'components/shared/ConfirmDialog';
import AppointmentRuleForm from './AppointmentRuleForm';

export const APPT_RULES_HEADING = 'Appointment Rules';
export const APPT_RULES_NOTE = 'Rules apply to appointments scheduled from this template.';
export const APPT_RULES_EMPTY = 'No appointment rules yet';
export const APPT_RULES_CREATE_MODE_DISABLED = 'Save the template first to manage appointment rules';
export const APPT_RULES_ADD_LABEL = 'Add Rule';
export const APPT_RULES_LOADING = 'Loading appointment rules…';
export const APPT_RULES_RETRY_LABEL = 'Retry';
export const APPT_RULES_STATUS_ACTIVE = 'Active';
export const APPT_RULES_STATUS_INACTIVE = 'Inactive';
export const APPT_RULES_DELETE_TITLE = 'Delete appointment rule?';
export const APPT_RULES_DELETE_MESSAGE =
  'This permanently deletes the rule. New appointments will no longer be checked against it; appointments already scheduled are not affected.';
export const APPT_RULES_DELETE_CONFIRM_LABEL = 'Delete rule';
export const APPT_RULES_NOTICE_UNAVAILABLE = 'Minimum notice — unavailable';

/** Humanized rule summary for list rows — never render raw configurationJson. */
export function formatMinimumNoticeHours(hours: number | null): string {
  return hours === null
    ? APPT_RULES_NOTICE_UNAVAILABLE
    : `Minimum notice — ${hours} hours`;
}

type EditorState = { mode: 'create' } | { mode: 'edit'; rule: AppointmentRule } | null;

type Props = {
  /** Saved template id; undefined renders the create-mode disabled state. */
  templateId: string | undefined;
  className?: string;
};

/**
 * Appointment Rules section of the workflow template editor. All CRUD state
 * lives in `useAppointmentRules` (M4); this component owns only UI state
 * (collapse, editor panel, pending delete). The hook re-syncs the list after
 * every mutation and toggles `isLoading` while doing so — the list therefore
 * stays mounted during refreshes (`aria-busy`) so editor input and focus are
 * never lost, and the loading panel only covers the initial fetch. The editor
 * closes only on success (on failure the hook's error is shown as the form
 * `serverError` and user input is preserved); the delete dialog closes on
 * success AND on failure so the inline error banner (role="alert") is never
 * hidden behind the modal backdrop.
 */
export default function AppointmentRulesSection({ templateId, className = '' }: Props) {
  const { rules, isLoading, error, load, create, update, toggle, remove } =
    useAppointmentRules(templateId);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [editor, setEditor] = useState<EditorState>(null);
  const [pendingDelete, setPendingDelete] = useState<AppointmentRule | null>(null);
  const [isMutating, setIsMutating] = useState<boolean>(false);
  const [focusRestoreCount, setFocusRestoreCount] = useState<number>(0);
  const editorTriggerRef = useRef<HTMLButtonElement | null>(null);

  // Return focus to the button that opened the editor, after the close has
  // been committed (the trigger may have been disabled during the mutation).
  useEffect(() => {
    if (focusRestoreCount === 0) return;
    editorTriggerRef.current?.focus();
  }, [focusRestoreCount]);

  const openCreateEditor = (e: MouseEvent<HTMLButtonElement>): void => {
    editorTriggerRef.current = e.currentTarget;
    setEditor({ mode: 'create' });
  };

  const openEditEditor = (rule: AppointmentRule, e: MouseEvent<HTMLButtonElement>): void => {
    editorTriggerRef.current = e.currentTarget;
    setEditor({ mode: 'edit', rule });
  };

  const closeEditor = (): void => {
    setEditor(null);
    setFocusRestoreCount((n) => n + 1);
  };

  const handleSave = async (
    payload: CreateAppointmentRuleRequest | UpdateAppointmentRuleRequest,
  ): Promise<void> => {
    if (!editor) return;
    setIsMutating(true);
    let ok = false;
    if (editor.mode === 'edit' && 'isActive' in payload) {
      ok = await update(editor.rule.id, payload);
    } else if (editor.mode === 'create' && !('isActive' in payload)) {
      ok = await create(payload);
    }
    setIsMutating(false);
    if (ok) closeEditor();
  };

  const handleToggle = async (rule: AppointmentRule): Promise<void> => {
    setIsMutating(true);
    await toggle(rule);
    setIsMutating(false);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!pendingDelete) return;
    setIsMutating(true);
    // Close on success and on failure: on failure the hook's error renders in
    // the inline role="alert" banner, which the open dialog's modal backdrop
    // would otherwise obscure.
    await remove(pendingDelete.id);
    setIsMutating(false);
    setPendingDelete(null);
  };

  const showInitialLoading = isLoading && rules.length === 0 && !editor;
  const showFullError = !showInitialLoading && error !== null && rules.length === 0 && !editor;

  return (
    <section
      className={`bg-gray-100 p-4 mt-6 rounded-lg ${className}`}
      aria-labelledby="appointment-rules-heading"
    >
      <div className="flex items-center justify-between">
        <h3 id="appointment-rules-heading" className="text-lg font-semibold text-gray-700">
          {APPT_RULES_HEADING}
        </h3>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="group p-1"
          aria-label={collapsed ? 'Expand appointment rules' : 'Collapse appointment rules'}
          aria-expanded={!collapsed}
        >
          <ChevronDownIcon
            className={`h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${
              collapsed ? '-rotate-90' : ''
            }`}
            aria-hidden="true"
          />
        </button>
      </div>

      {!collapsed && (
        <div className="mt-4 space-y-4">
          {!templateId ? (
            <p className="text-sm text-gray-500">{APPT_RULES_CREATE_MODE_DISABLED}</p>
          ) : (
            <>
              <p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-gray-700">
                {APPT_RULES_NOTE}
              </p>

              {showInitialLoading && (
                <div
                  role="status"
                  aria-busy="true"
                  className="rounded-lg border border-gray-200 bg-white p-4"
                >
                  <p className="text-sm text-gray-500">{APPT_RULES_LOADING}</p>
                </div>
              )}

              {showFullError && (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-red-700">{error}</p>
                  <button
                    type="button"
                    onClick={() => void load()}
                    className="mt-2 inline-flex items-center justify-center rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    {APPT_RULES_RETRY_LABEL}
                  </button>
                </div>
              )}

              {!showInitialLoading && !showFullError && (
                <>
                  {error && !editor && (
                    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
                      <p className="text-sm text-red-700">{error}</p>
                      <button
                        type="button"
                        onClick={() => void load()}
                        className="mt-2 inline-flex items-center justify-center rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        {APPT_RULES_RETRY_LABEL}
                      </button>
                    </div>
                  )}

                  {rules.length === 0 && !editor && (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-white p-4">
                      <p className="text-sm text-gray-500">{APPT_RULES_EMPTY}</p>
                      <button
                        type="button"
                        onClick={openCreateEditor}
                        className="mt-3 inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {APPT_RULES_ADD_LABEL}
                      </button>
                    </div>
                  )}

                  {rules.length > 0 && (
                    <ul className="space-y-2" aria-busy={isLoading}>
                      {rules.map((rule) => (
                        <li
                          key={rule.id}
                          className="flex items-center justify-between gap-3 rounded-lg border border-gray-300 bg-white p-3"
                        >
                          <div className={rule.isActive ? 'min-w-0' : 'min-w-0 opacity-60'}>
                            <p className="text-sm font-medium text-gray-800">
                              {formatMinimumNoticeHours(rule.minimumNoticeHours)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {appointmentRuleTypeLabels[rule.ruleType]}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-3">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                rule.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-200 text-gray-600'
                              }`}
                            >
                              {rule.isActive
                                ? APPT_RULES_STATUS_ACTIVE
                                : APPT_RULES_STATUS_INACTIVE}
                            </span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={rule.isActive}
                              aria-label={rule.isActive ? 'Deactivate rule' : 'Activate rule'}
                              disabled={isMutating || rule.minimumNoticeHours === null}
                              onClick={() => void handleToggle(rule)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40 ${
                                rule.isActive ? 'bg-blue-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                aria-hidden="true"
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                  rule.isActive ? 'translate-x-4' : 'translate-x-0.5'
                                }`}
                              />
                            </button>
                            <button
                              type="button"
                              aria-label="Edit Rule"
                              disabled={isMutating || editor !== null}
                              onClick={(e) => openEditEditor(rule, e)}
                              className="text-blue-400 hover:text-blue-600 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              aria-label="Delete Rule"
                              disabled={isMutating}
                              onClick={() => setPendingDelete(rule)}
                              className="text-red-400 hover:text-red-600 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {rules.length > 0 && !editor && (
                    <div>
                      <button
                        type="button"
                        onClick={openCreateEditor}
                        disabled={isMutating}
                        className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {APPT_RULES_ADD_LABEL}
                      </button>
                    </div>
                  )}
                </>
              )}

              {editor && (
                <div className="rounded-lg border border-blue-300 bg-blue-50 p-3">
                  <AppointmentRuleForm
                    initialValue={editor.mode === 'edit' ? editor.rule : null}
                    isSubmitting={isMutating}
                    serverError={error}
                    onSave={(payload) => void handleSave(payload)}
                    onCancel={closeEditor}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title={APPT_RULES_DELETE_TITLE}
        message={APPT_RULES_DELETE_MESSAGE}
        confirmLabel={APPT_RULES_DELETE_CONFIRM_LABEL}
        isDestructive
        isConfirming={isMutating}
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={() => setPendingDelete(null)}
      />
    </section>
  );
}
